import * as XLSX from 'xlsx';
import withAuthHeader from '../../utils/withAuthHeader';
import checkAuthorizationError from '../../utils/checkAuthorizationError';
import {
  mapFetchOrderDetailResponse,
  mapFetchOrdersListRequest,
  mapFetchOrdersListResponse
} from '../mappers/orders';

// Colunas do relatório personalizado na ordem desejada para o Excel
const RELATORIO_VENDAS_COLUNAS = [
  'DescontoValorModalidadeRetirada',
  'app',
  'cashback-acúmulo',
  'cep',
  'comentario',
  'cpf',
  'crédito-digital-gasto',
  'cupom-utilizado',
  'data',
  'data-nascimento',
  'dist-pedido-(km)',
  'endereço-delivery',
  'forma-de-pagamento',
  'id-comanda',
  'ip',
  'itens',
  'local-pagamento',
  'logística',
  'loja',
  'modalidade',
  'nota',
  'num-da-compra',
  'plataforma',
  'saldo-consumo',
  'status-pedido',
  'tempo-entre-compras',
  'user-data-cadastro',
  'user-email',
  'user-id',
  'user-nome',
  'usuário_total-de-compras',
  'valor-desconto',
  'valor-estorno',
  'valor-frete',
  'valor-frete-descontado',
  'valor-líquido_(pago - frete)',
  'valor-transacionado-(o que foi pago)'
];

// Normaliza nome de coluna para matching: lowercase, sem acentos, só letras e números
// Assim "ValorFrete", "valor-frete" e "Valor Frete" viram "valorfrete"
const normalizarChave = (str) => {
  if (str == null) return '';
  return String(str)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '');
};

// Dado um objeto linha da API, monta um objeto só com as chaves em RELATORIO_VENDAS_COLUNAS na ordem correta
const mapLinhaParaColunasOrdenadas = (linha) => {
  const keysNorm = {};
  Object.keys(linha || {}).forEach((k) => {
    keysNorm[normalizarChave(k)] = k;
  });
  const saida = {};
  RELATORIO_VENDAS_COLUNAS.forEach((col) => {
    const val = linha[col];
    if (val !== undefined && val !== null) {
      saida[col] = val;
      return;
    }
    const colNorm = normalizarChave(col);
    const keyOriginal = keysNorm[colNorm];
    if (keyOriginal !== undefined) {
      saida[col] = linha[keyOriginal];
    } else {
      saida[col] = '';
    }
  });
  return saida;
};

// Extrai dados da planilha - tenta header na 1ª linha; se vazio, usa sheet_to_json padrão
const planilhaParaDados = (worksheet) => {
  const raw = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' });
  if (!raw.length) return { colunas: [], linhas: [] };
  const colunas = raw[0].map((c, i) => (c != null && c !== '' ? String(c).trim() : `Col${i}`));
  const linhas = raw.slice(1).map((row) => {
    const obj = {};
    colunas.forEach((col, i) => { obj[col] = row[i] !== undefined && row[i] !== null ? row[i] : ''; });
    return obj;
  });
  if (linhas.length > 0) return { colunas, linhas };
  // Fallback: sheet_to_json usa a primeira linha como chaves
  const fallback = XLSX.utils.sheet_to_json(worksheet, { defval: '' });
  const cols = fallback[0] ? Object.keys(fallback[0]) : [];
  return { colunas: cols, linhas: fallback };
};

export const fetchOrdersList = (filters) => async (dispatch, getState, api) => {
  let result = {};
  try {
    const url = `api/v1/dashboard/pedidos`;
    const body = mapFetchOrdersListRequest({
      initialDate: filters.initialDate,
      finalDate: filters.finalDate,
      searchTerm: filters.searchTerm,
      storeId: filters?.storeId,
      id: filters.id,
      modality: filters.modality,
      schedule: filters.schedule
    });
    const apiResponse = await api.get(url, {
      params: body,
      ...withAuthHeader()
    });
    const response = mapFetchOrdersListResponse(apiResponse.data);
    result = {
      success: true,
      data: response
    };
  } catch (e) {
    checkAuthorizationError(e, dispatch);
    result = {
      error: e.message
    };
  }
  return result;
};

export const fetchOrderDetail = (orderId) => async (dispatch, getState, api) => {
  let result = {};
  try {
    const url = `api/v1/dashboard/pedidos/${orderId}`;
    const apiResponse = await api.get(url, {
      ...withAuthHeader()
    });
    let response = mapFetchOrderDetailResponse(apiResponse.data);

    if (!response.refund.isAvailable) {
      const refundStatusResponse = await fetchRefundStatus(response.cardId, api);

      if (refundStatusResponse.success) {
        response = {
          ...response,
          refund: { ...response.refund, status: refundStatusResponse.data }
        };
      }
    }

    result = {
      success: true,
      data: response
    };
  } catch (e) {
    checkAuthorizationError(e, dispatch);
    result = {
      error: e.message
    };
  }
  return result;
};

export const postRefund = (data) => async (dispatch, getState, api) => {
  let result = {};
  try {
    const url = `api/v1/dashboard/pedidos/estornar`;
    const { cardId, total, motivation } = data;
    const response = await api.post(
      url,
      { ComandaId: cardId, ValorAEstornar: total, MotivoEstornoId: motivation },
      {
        ...withAuthHeader()
      }
    );

    result = {
      success: true,
      data: response
    };
  } catch (e) {
    checkAuthorizationError(e, dispatch);
    result = {
      error: e.message
    };
  }
  return result;
};

export const downloadRelatorioVendas = (filters) => async (dispatch, getState, api) => {
  let result = {};
  try {
    const url = 'api/v1/dashboard/relatorioVendas';
    const params = mapFetchOrdersListRequest({
      initialDate: filters.initialDate,
      finalDate: filters.finalDate,
      storeId: filters?.storeId,
      searchTerm: filters?.searchTerm,
      id: filters?.id,
      modality: filters?.modality,
      schedule: filters?.schedule
    });
    const apiResponse = await api.get(url, {
      params,
      ...withAuthHeader(),
      responseType: 'arraybuffer'
    });
    const buffer = apiResponse.data;
    let linhas = [];

    // Tenta interpretar como JSON (caso a API retorne array de objetos)
    try {
      const decoder = new TextDecoder('utf-8');
      const text = decoder.decode(new Uint8Array(buffer));
      const parsed = JSON.parse(text);
      const data = Array.isArray(parsed) ? parsed : (parsed?.data ?? parsed?.itens ?? []);
      if (Array.isArray(data) && data.length > 0) {
        linhas = data;
      }
    } catch (_) {
      // Não é JSON, segue como XLSX
    }

    // Se não veio do JSON, lê como planilha Excel
    if (linhas.length === 0) {
      const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
      const firstSheet = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheet];
      if (worksheet) {
        const extraido = planilhaParaDados(worksheet);
        linhas = extraido.linhas || [];
      }
    }

    const dadosOrdenados = (linhas && linhas.length > 0)
      ? linhas.map(mapLinhaParaColunasOrdenadas)
      : [RELATORIO_VENDAS_COLUNAS.reduce((acc, col) => ({ ...acc, [col]: '' }), {})];
    const planilha = XLSX.utils.json_to_sheet(dadosOrdenados, { header: RELATORIO_VENDAS_COLUNAS });
    const wbOut = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wbOut, planilha, 'Relatório Vendas');
    const contentDisposition = apiResponse.headers['content-disposition'];
    let fileName = 'relatorio-vendas.xlsx';
    if (contentDisposition) {
      const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
      if (match && match[1]) fileName = match[1].replace(/['"]/g, '').trim();
    }
    if (!fileName.toLowerCase().endsWith('.xlsx')) fileName = fileName.replace(/\.[^.]+$/, '') || fileName + '.xlsx';
    XLSX.writeFile(wbOut, fileName);
    result = { success: true };
  } catch (e) {
    checkAuthorizationError(e, dispatch);
    if (e.response?.data && typeof e.response.data.text === 'function') {
      try {
        const errorText = await e.response.data.text();
        const parsed = JSON.parse(errorText);
        result = { error: parsed?.message || parsed?.error || errorText };
      } catch {
        result = { error: e.message };
      }
    } else {
      result = { error: e.message };
    }
  }
  return result;
};

const fetchRefundStatus = async (orderId, api) => {
  let result = {};
  try {
    const url = `api/v1/dashboard/pedidos/obterStatusEstorno/${orderId}`;
    const apiResponse = await api.get(url, {
      ...withAuthHeader()
    });

    result = {
      success: true,
      data: apiResponse
    };
  } catch (e) {
    // checkAuthorizationError(e, dispatch);
    result = {
      error: e.message
    };
  }
  return result;
};
